/* TimeSlotSelect — iter98.99n
 *
 * Reusable time-slot dropdown used across the doctor portal in place of
 * the native `<input type="time">` spinner. Matches what Calendly,
 * Cal.com and Google Calendar do:
 *
 *   - Pre-snapped 15-minute slots in 12-hour format ("1:30 PM").
 *   - Past slots are filtered out when the chosen `date` is today.
 *   - Configurable working-hour range via `minHour` / `maxHour`.
 *   - Graceful "fully booked today" empty state — caller can opt out
 *     of the auto-jump-to-tomorrow chip by setting `allowEmpty={true}`.
 *
 * Designed to slot directly into existing forms (h-10 trigger, matches
 * shadcn `<Input>` height). Renders nothing if `slots` resolves empty
 * AND the caller passes `allowEmpty` — caller is then responsible for
 * a fallback UI (date bump, etc).
 */
import React, { useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/* Build the slot grid once per (minHour, maxHour, step) tuple. */
const buildSlots = (minHour, maxHour, stepMins) => {
    const out = [];
    for (let h = minHour; h <= maxHour; h += 1) {
        for (let m = 0; m < 60; m += stepMins) {
            // Cap the final hour: don't emit slots that wrap past
            // maxHour:45 (e.g. when maxHour=21 we emit up to 21:45).
            out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
        }
    }
    return out;
};

/* Current local time as "HH:MM" — used for past-slot filtering. */
const nowHHMM = () => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

/* Local-calendar today as "YYYY-MM-DD" — matches what date inputs use. */
const todayISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/* Format "13:30" → "1:30 PM". */
export const fmtSlotHuman = (hhmm) => {
    if (!hhmm) return "";
    const [hStr, mStr] = hhmm.split(":");
    let h = parseInt(hStr, 10);
    const period = h >= 12 ? "PM" : "AM";
    h = h % 12;
    if (h === 0) h = 12;
    return `${h}:${mStr} ${period}`;
};

export default function TimeSlotSelect({
    value,
    onChange,
    date = null,                 // when this equals today, past slots are filtered
    minHour = 7,
    maxHour = 21,
    stepMins = 15,
    className = "h-10",
    testId = "time-slot-select",
    allowEmpty = false,           // if true, returns null when fully past
    emptyLabel = "Fully booked today · pick a future date",
    onEmptyJump = null,           // optional callback when user clicks the empty pill
    disabled = false,
}) {
    const allSlots = buildSlots(minHour, maxHour, stepMins);
    const isToday = date && date === todayISO();
    const slots = isToday ? allSlots.filter((t) => t >= nowHHMM()) : allSlots;

    // If the currently-selected slot dropped out of the list (e.g. it
    // just passed in real-time), snap to the next valid slot so the
    // <Select> never shows an empty trigger.
    useEffect(() => {
        if (slots.length === 0) return;
        if (!slots.includes(value)) {
            onChange?.(slots[0]);
        }
    }, [slots, value, onChange]);

    if (slots.length === 0) {
        if (allowEmpty) return null;
        return (
            <button
                type="button"
                onClick={onEmptyJump || undefined}
                disabled={disabled || !onEmptyJump}
                className={`w-full text-[13px] rounded-md border border-dashed border-primary/40 bg-primary/5 text-primary font-semibold px-3 outline-none hover:bg-primary/10 transition-colors ${className}`}
                data-testid={`${testId}-empty`}
            >
                {emptyLabel}
            </button>
        );
    }

    return (
        <Select value={slots.includes(value) ? value : slots[0]} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger className={className} data-testid={testId}>
                <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72">
                {slots.map((t) => (
                    <SelectItem key={t} value={t}>{fmtSlotHuman(t)}</SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}
